import Box from '@mui/material/Box';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import ListItemText from '@mui/material/ListItemText';

import * as booleanButtonStyles from './BooleanButton.styles';
import BooleanButtonAffix from './components/BooleanButtonAffix';
import Icon from './components/Icon';
import {
  BooleanButtonCheckboxProps,
  BooleanButtonImageProps,
  BooleanButtonProps,
  BooleanButtonRadioProps,
  IconType,
} from './BooleanButton.types';

function BooleanButton({
  title,
  subtitle,
  selected,
  onClick,
  endAdornment,
  disabled,
  affix,
  dense,
  type,
  iconProps,
  displayAsRow,
  bottomMargin = true,
  ...moreProps
}: BooleanButtonProps) {
  const { classes: listItemButtonStyle } = booleanButtonStyles.useButtonContentStyles({
    displayAsRow: displayAsRow || type === IconType.IMAGE,
  });
  const { classes: allBooleanButtonClasses } = booleanButtonStyles.useBooleanButtonStyles({ bottomMargin });

  const titleString = typeof title === 'string' ? title.toString() : undefined;
  const ariaLabel = moreProps['aria-label'] ?? titleString;

  return (
    <ListItem
      dense={dense}
      role={undefined}
      button
      disableRipple
      disableGutters
      selected={selected}
      onClick={onClick}
      disabled={!!disabled}
      classes={allBooleanButtonClasses}
      {...moreProps}
    >
      <ListItemIcon classes={{ root: listItemButtonStyle.root }}>
        <Icon aria-label={ariaLabel} {...iconProps} type={type} checked={selected} />
      </ListItemIcon>
      <Box alignItems="center" flex="1 1 auto" justifyContent="space-between" className={listItemButtonStyle.grid}>
        <Box>
          <ListItemText
            primaryTypographyProps={{ variant: 'h6' }}
            secondaryTypographyProps={{ variant: 'body1', color: 'inherit' }}
            primary={title}
            secondary={subtitle}
            className={listItemButtonStyle.noMargin}
          />
        </Box>
        <BooleanButtonAffix selected={selected}>{affix}</BooleanButtonAffix>
      </Box>
      {!!endAdornment && (
        <ListItemSecondaryAction sx={({ spacing }) => ({ right: spacing(2) })}>{endAdornment}</ListItemSecondaryAction>
      )}
    </ListItem>
  );
}

/**
 *
 * Wrapping and exporting buttons for Storybook
 *
 */

export default BooleanButton;

const CheckboxButton = (props: Omit<BooleanButtonCheckboxProps, 'type'>) => (
  <BooleanButton {...props} type={IconType.CHECKBOX} />
);

const RadioButton = (props: Omit<BooleanButtonRadioProps, 'type'>) => (
  <BooleanButton {...props} type={IconType.RADIO} />
);

const ImageIconButton = (props: Omit<BooleanButtonImageProps, 'type'>) => (
  <BooleanButton {...props} type={IconType.IMAGE} />
);

export { CheckboxButton, RadioButton, ImageIconButton };
